import { Link } from "react-router-dom"
import { useState, useEffect } from "react"
import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { MapPin, Phone, Clock, ArrowRight , Calendar} from "lucide-react"
import SEO from "./SEO"
 
export default function Locations() {
  const [seo, setSeo] = useState({});
  const [pageKeywords, setPageKeywords] = useState("");

  useEffect(() => {
    fetch("/wp-json/custom/v1/page-seo?slug=locations")
      .then(res => res.json())
      .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
      .catch(() => {});
  }, []);

  const locationsPageSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://pranaamhospitals.com/#organization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/images/pranaam-logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad providing quality healthcare across multiple locations.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1-58/6/40 & 41, Mythri Nagar, Madinaguda, Miyapur",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500050",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "email": "info@pranaamhospitals.in",
        "availableLanguage": [
          "English",
          "Telugu",
          "Hindi"
        ]
      },
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": [
          "English",
          "Telugu",
          "Hindi"
        ]
      }
    ],
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://pranaamhospitals.com/locations#breadcrumb",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://pranaamhospitals.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Locations",
        "item": "https://pranaamhospitals.com/locations"
      }
    ]
  }
];
 
  const locations = [
    {
      id: "madinaguda",
      name: "Madinaguda Branch",
      address: "1-58/6/40 & 41, Mythri Nagar, Madinaguda, Miyapur, Hyderabad – 500050, Telangana, India",
      phone: "+91 40 2304 2777",
      mobile: "+91 99660 60000",
      email: "info@pranaamhospitals.in",
      hours: "Open 24x7",
      image: "/wp-content/uploads/2026/05/PranaamBuilding.webp",
    },
    {
      id: "nallagandla",
      name: "Nallagandla Branch",
      address:
        "Survey No. 369, 7-1/31/1, Kanchi Gachibowli Road, Nallagandla, Serilingampally, Hyderabad, Telangana – 500019",
      phone: "+91 91212 42844",
      email: "info@pranaamhospitals.in",
      hours: "Open 24x7",
      image: "/wp-content/uploads/2026/05/NallagandlaHospitalBuilding.webp",
    },
    {
      id: "zaheerabad",
      name: "Zaheerabad Branch",
      address:
        "Platinum Pride Commercial Complex, GHMC No: 2-5-292/3, Rachannapet, Mogadampally Cross Road, Sangareddy District, Zaheerabad – 502220",
      phone: "+91 77998 28844",
      email: "info@pranaamhospitals.in",
      hours: "Open 24x7",
      image: "/wp-content/uploads/2026/05/zaheerabad-branch.webp",
    },
    {
      id: "guntur",
      name: "Guntur Branch",
      address:
        "Palakaluru Rd, Peda Palakaluru, Andhra Pradesh 522009",
      phone: "+91 77998 18844",
      email: "info@pranaamhospitals.in",
      hours: "Open 24x7",
      image: "/wp-content/uploads/2026/06/Gunture-1.pnguilding.webp",
    },
  ]
 
  return (
    <>
    <SEO
      title={seo.title || "Hospital Locations in Hyderabad | Pranaam Hospitals Branches"}
      description={seo.description || "Find Pranaam Hospitals locations across Hyderabad including Madeenaguda, Nallagandla, Isnapur, and Zaheerabad. Get addresses, contact numbers, directions, and services offered at each branch."}
      canonical={seo.canonical || "https://pranaamhospitals.com/locations"}
      robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
      keywords={pageKeywords}
      ogTitle={seo.og_title}
      ogDescription={seo.og_description}
      ogImage={seo.og_image || seo.featured_image}
      twitterTitle={seo.twitter_title}
      twitterDescription={seo.twitter_description}
      author="Pranaam Hospitals"
      publisher="Pranaam Hospitals"
      schema={locationsPageSchema}
    />
   <main className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-primary/80 py-16 md:py-24">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              <MapPin className="w-5 h-5 mr-2" />
              Our Locations
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Our Hospital Locations</h1>
            <p className="text-lg text-white/90 mb-6">
              Pranaam Hospitals is committed to providing exceptional healthcare services across multiple locations in
              Hyderabad. Find the branch nearest to you.
            </p>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 h-16 bg-white"
          style={{ clipPath: "polygon(0 100%, 100% 100%, 100% 0)" }}
        ></div>
      </section>
 
      {/* Locations Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {locations.map((location) => (
              <Card key={location.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-64">
                  <img
                    src={location.image}
                    alt={`${location.name} Pranaam Hospitals`}
                    width="800"
                    height="500"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">{location.name}</h2>
 
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start">
                      <MapPin className="h-7 w-7 text-primary mr-3 mt-0.5" />
                      <p className="text-gray-600">{location.address}</p>
                    </div>
 
                    <div className="flex items-start">
                      <Phone className="h-5 w-5 text-primary mr-3 mt-0.5" />
                      <div>
                        <p className="text-gray-600">
                          {location.phone}
                          {location.mobile && (
                            <>
                              <br />
                              {location.mobile}
                            </>
                          )}
                        </p>
                        <p className="text-gray-600 mt-1">{location.email}</p>
                      </div>
                    </div>
 
                    <div className="flex items-start">
                      <Clock className="h-5 w-5 text-primary mr-3 mt-0.5" />
                      <p className="text-gray-600">{location.hours}</p>
                    </div>
 
                    {location.landmarks && (
                      <div className="flex items-start">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-7 w-7 text-primary mr-3 mt-0.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <p className="text-gray-600">Landmarks: {location.landmarks}</p>
                      </div>
                    )}
                  </div>
 
                  <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
                   <Button
                      asChild
                      className="w-full sm:w-auto min-h-[48px] px-5 bg-primary hover:bg-primary/90"
                    >
                      <Link
                        to={`/locations/${location.id}`}
                        aria-label={`View details about ${location.name}`}
                        className="flex items-center justify-center gap-2"
                      >
                        View Details About {location.name}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button size="lg" className="w-full sm:w-auto" asChild>
                        <a
                          href="/book-now"
                          rel="noopener noreferrer"
                        >
                          <Calendar className="mr-2 h-5 w-5" />
                          Book an Appointment
                        </a>
                      </Button>
                   
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
 
      {/* Map Section */}
      {/* <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Find Us on the Map</h2>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <iframe
              src="https://www.google.com/maps/d/embed?mid=1EXAMPLE_MAP_ID&ehbc=2E312F"
              width="100%"
              height="480"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section> */}
 
      {/* CTA Section */}
      <section className="py-12 bg-primary">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0">
              <h2 className="text-2xl md:text-3xl font-bold text-white">Need help finding us?</h2>
              <p className="text-white/90 mt-2">Contact our team for directions or any other assistance.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
             <Button
                asChild
                size="lg"
                className="min-h-[48px] px-6 bg-white text-primary hover:bg-gray-100"
              >
                <Link
                  to="/contact"
                  aria-label="Contact Pranaam Hospitals"
                  className="flex items-center justify-center"
                >
                  Contact Us
                </Link>
              </Button>

              <Button
                asChild
                variant="outline"
                size="lg"
                className="min-h-[48px] px-6 bg-white text-primary hover:bg-gray-100"
              >
                <a
                  href="tel:+919121242844"
                  aria-label="Call Pranaam Hospitals"
                  className="flex items-center justify-center gap-2"
                >
                  <Phone className="h-4 w-4" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  )
}